import React, { useEffect, useState } from "react";
import { Icon } from "@iconify/react";
import { Tooltip } from "react-tooltip";
import AnalyticsSection from "./AnalyticsSection";
import PanelHeadline, { Fig } from "./PanelHeadline";
import {
    fetchChatToPurchaseStats,
    fetchMatchToChatStats,
} from "../services/analyticsService";

const ChatConversionStats = ({ startLoading, stopLoading }) => {
    // const [loading, setLoading] = useState(false);
    const [matchToChat, setMatchToChat] = useState(null);
    const [chatToPurchase, setChatToPurchase] = useState(null);
    // 🔴 A FAILED FETCH IS NOT A ZERO. Both cards used to fall back to 0% when
    // the request threw, so a broken endpoint read as a real business number
    // and nothing on screen told the operator the difference.
    const [loadError, setLoadError] = useState(false);

    useEffect(() => {
        loadStats();
    }, []);

    const loadStats = async () => {
        try {
            startLoading(); 
            
            const [matchChatRes, chatPurchaseRes] = await Promise.all([
                fetchMatchToChatStats(),
                fetchChatToPurchaseStats(),
            ]);
            setMatchToChat(matchChatRes?.data?.data);
            setChatToPurchase(chatPurchaseRes?.data?.data);
        } catch (err) {
            console.error("Failed to load conversion stats", err);
            setLoadError(true);
        } finally {
           stopLoading();
        }
    };

    // if (loading) {
    //     return <div className="text-center py-4">Loading analytics…</div>;
    // }

    const cards = [
        {
            title: "Connections → Chat",
            value: loadError ? "Not available" : `${matchToChat?.percentage ?? 0}%`,
            description: loadError
                ? "These numbers could not be loaded. This is not a zero."
                : `${matchToChat?.chatStartedCount || 0} chats started from ${matchToChat?.matchedCount || 0
                } total connections`,
            method:
                "Counts every connection ever made against those where at least one message was sent. Lifetime, not weekly.",
            icon: "mdi:chat-processing",
            bg: "bg-purple",
            gradient: "bg-gradient-start-2",
        },
        {
            title: "Chat → Purchase",
            value: loadError ? "Not available" : `${chatToPurchase?.percentage ?? 0}%`,
            description: loadError
                ? "These numbers could not be loaded. This is not a zero."
                : `${chatToPurchase?.purchasedUserCount || 0
                } purchases from ${chatToPurchase?.chatUserCount || 0
                } users who started chat`,
            method:
                "Counts members who started a chat against those who then bought anything. Lifetime, not weekly.",
            icon: "mdi:wallet",
            bg: "bg-success-main",
            gradient: "bg-gradient-start-4",
        },
    ];

    return (
        <AnalyticsSection
            id="chatconv"
            icon="mdi:chat-processing-outline"
            title="Chat conversion"
            basis="Both figures are lifetime, not weekly, so they will never reconcile with the weekly counts in the loop panel above."
        >
            <PanelHeadline
                failed={loadError}
                failedText="Could not load conversion. Both figures below are unavailable, not zero."
                empty={!loadError && !matchToChat && !chatToPurchase}
                emptyText="No connections recorded yet."
            >
                <Fig>{matchToChat?.percentage ?? 0}%</Fig> of connections become a
                chat (<Fig>{matchToChat?.chatStartedCount ?? 0}</Fig> of{" "}
                <Fig>{matchToChat?.matchedCount ?? 0}</Fig>), and{" "}
                <Fig>{chatToPurchase?.percentage ?? 0}%</Fig> of those members go on
                to buy.
            </PanelHeadline>

            <div className="row row-cols-lg-2 row-cols-1 gy-4 mb-10">
                {cards.map((card, idx) => (
                    <div className="col" key={idx}>
                        <div
                            className={`card shadow-none border ${card.gradient} h-100 position-relative`}
                        >
                            <div className="card-body p-20 position-relative">
                                <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
                                    <div>
                                        <p className="fw-medium text-primary-light mb-1">
                                            {card.title}
                                        </p>
                                        <h6 className="mb-0">{card.value}</h6>
                                        {/* 🔴 THE DENOMINATOR IS THE READING, so it sits
                                            inline at rest. It used to live only in the
                                            tooltip below, which meant a percentage was
                                            on screen with no way to see what it counted
                                            without hovering an 8px dot. */}
                                        <p className="text-sm text-secondary-light mb-0 mt-1">
                                            {card.description}
                                        </p>
                                    </div>

                                    <div
                                        className={`w-50-px h-50-px ${card.bg} rounded-circle d-flex justify-content-center align-items-center`}
                                    >
                                        <Icon
                                            icon={card.icon}
                                            className="text-white text-2xl mb-0"
                                        />
                                    </div>
                                </div>

                                {/* Tooltip icon */}
                                <span
                                    className={`position-absolute ${card.bg} rounded-circle d-flex justify-content-center align-items-center`}
                                    style={{
                                        bottom: "8px",
                                        right: "8px",
                                        cursor: "pointer",
                                    }}
                                    data-tooltip-id={`tooltip-conversion-${idx}`}
                                >
                                    <Icon
                                        icon="ic:baseline-info"
                                        className="text-white"
                                        style={{ fontSize: "1rem" }}
                                    />
                                </span>

                                <Tooltip
                                    id={`tooltip-conversion-${idx}`}
                                    place="top"
                                    style={{ maxWidth: 340, zIndex: 1080 }}
                                >
                                    {card.method}
                                </Tooltip>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </AnalyticsSection>
    );
};

export default ChatConversionStats;
